import { keyframes } from '@emotion/react';
import { SystemStyleObject } from '@styled-system/css';
import * as React from 'react';
import { VisuallyHidden } from 'reakit';
import { variant } from 'styled-system';
import { createComponent, createHook } from 'reakit-system';
import { useBox, BoxHTMLProps } from '../Box/Box';
import { cx, ecss } from '../reexports/emotion';
import { StylesOptions } from '../hooks/useStyles';

const wave = keyframes`
  0%,
  100% {
    box-shadow: 0 10px 0 -3px;
  }
  50% {
    box-shadow: 0 10px 0 0;
  }
`;

const overlay = variant({
  prop: 'overlay',
  variants: {
    visible: { background: 'rgba(255, 255, 255, .8)' },
    invisible: { background: 'none' },
  },
});

const indicatorDotStyles = {
  fontSize: '10px',
  textIndent: '-9999em',
  size: 10,
  borderRadius: 'round',
  animationFillMode: 'none',
  animation: `${wave} .8s infinite ease-in-out`,
  animationDelay: '.16s',
} as SystemStyleObject;

type OverlayOptions = {
  overlay: 'visible' | 'invisible';
} & StylesOptions;

const useOverlay = createHook<OverlayOptions, BoxHTMLProps>({
  name: 'Overlay',
  keys: ['overlay'],
  compose: [useBox],
  useOptions(options) {
    return {
      ...options,
      position: 'absolute',
      size: '100%',
      top: 0,
      left: 0,
      zIndex: 1,
    };
  },
  useProps(options, props) {
    return {
      ...props,
      className: cx(
        // @ts-expect-error
        props.className,
        ecss(
          {
            label: 'LoaderOverlay',
          },
          overlay(options)
        )
      ),
    };
  },
});

const Overlay = createComponent({
  as: 'div',
  useHook: useOverlay,
  memo: true,
});

const useIndicator = createHook<{}, BoxHTMLProps>({
  name: 'Indicator',
  compose: [useBox],
  useOptions(options) {
    return {
      ...options,
      position: 'relative',
      display: 'block',
      size: 10,
      mx: 'auto',
      my: 'sp-0',
      color: 'gray-60',
      borderRadius: 'round',
      styles: {
        '&::before, &::after': {
          content: "''",
          position: 'absolute',
        },
        '&, &::before, &::after': indicatorDotStyles,
        '&::before': {
          left: '-24px',
          animationDelay: '.32s',
        },
        '&::after': {
          left: '24px',
          animationDelay: '0s',
        },
      },
    };
  },
  useProps(options, props) {
    return {
      ...props,
      className: cx(
        // @ts-expect-error
        props.className,
        ecss({
          label: 'LoaderIndicator',
        })
      ),
    };
  },
});

const Indicator = createComponent({
  as: 'div',
  useHook: useIndicator,
  memo: true,
});

/**
 * @beta
 */
export type LoaderProps = {
  overlayVisible: boolean;
  messageText?: string;
} & BoxHTMLProps &
  StylesOptions;

/**
 * @beta
 */
export const Loader = ({
  overlayVisible,
  messageText,
  ...props
}: LoaderProps) => (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Overlay overlay={overlayVisible ? 'visible' : 'invisible'} {...props}>
      <Indicator />
      <VisuallyHidden aria-live="polite">
        {messageText || 'Идёт загрузка.'}
      </VisuallyHidden>
    </Overlay>
  );
